import { type JSX, useCallback, useEffect, useState } from 'react';

import { hausdaten as holeHausdaten, hausdatenSetzen, type Hausdaten } from '../api/rechnungen';
import { ApiFehler } from '../api/fehler';
import { Feldraster } from '../komponenten/Akte';
import { Einstellungsseite, Thema } from '../komponenten/Einstellungen';
import { Meldung } from '../komponenten/Meldung';

/**
 * Die Geschäftsdaten des Hauses (§ 14 UStG, § 5 TMG, RNG-13).
 *
 * Sie standen bisher nur in der Datenbank und ließen sich allein über die
 * Route setzen. Das war für den ersten Beleg gerade noch tragbar und danach
 * nicht mehr: Eine Firmierung, eine Steuernummer oder eine geänderte
 * Bankverbindung sind gewöhnliche Vorgänge, keine Wartungsarbeiten.
 *
 * ============================================================================
 * EINE MASKE FÜR DEN GANZEN SATZ
 * ============================================================================
 *
 * `fach.hausdaten` ist **eine** Zeile, und die Route ersetzt sie vollständig.
 * Zwei Masken, die je einen Teil davon speichern, überschrieben einander: Wer
 * A öffnet, dann B ändert und speichert, und danach A speichert, hat B's
 * Änderung verloren — ohne Fehlermeldung. Deshalb liegt hier alles, und die
 * E-Rechnungsmaske zeigt die Bankfelder nur noch an.
 *
 * ============================================================================
 * WO JEDES FELD LANDET
 * ============================================================================
 *
 * Das steht an jedem Feld, weil es die Frage beantwortet, die man sich beim
 * Ausfüllen stellt: Manche Angaben erscheinen auf dem gedruckten Beleg (als
 * Platzhalter `haus.…`), manche gehen in die strukturierte E-Rechnung (mit
 * ihrer BT-Nummer), und ein paar tun beides. Eine Angabe, von der niemand
 * weiß, wohin sie geht, wird entweder nicht gepflegt oder falsch.
 *
 * SEITENTYP EINSTELLUNGEN (ÄA-41): je Thema eine Karte mit Feldraster. Der
 * Knopf „Geschäftsdaten speichern" bleibt EINER unter allen Karten — die
 * Route ersetzt den ganzen Satz, ein Knopf je Karte verspräche, nur diesen
 * Teil zu sichern.
 */

/** Ein leerer Satz — für den Fall, dass noch nie etwas hinterlegt wurde. */
const LEER: Hausdaten = {
  firmierung: '',
  strasse: '',
  plz: '',
  ort: '',
  land: 'DE',
  steuernummer: null,
  ustidnr: null,
  telefon: null,
  email: null,
  webseite: null,
  registergericht: null,
  registernummer: null,
  geschaeftsfuehrung: null,
  bankverbindung: null,
  iban: null,
  bic: null,
  kontoinhaber: null,
};

export function Geschaeftsdaten({
  darfPflegen,
}: {
  readonly darfPflegen: boolean;
}): JSX.Element {
  const [daten, setzeDaten] = useState<Hausdaten>(LEER);
  const [bestand, setzeBestand] = useState(false);
  const [laeuft, setzeLaeuft] = useState(false);
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  const laden = useCallback(async (): Promise<void> => {
    const geladen = await holeHausdaten();
    setzeBestand(geladen !== null);
    setzeDaten(geladen ?? LEER);
  }, []);

  useEffect(() => {
    void laden().catch((ursache: unknown) => setzeFehler(ursache as ApiFehler));
  }, [laden]);

  /** Ein Textfeld. `null` und leer sind dasselbe — die Datenbank kennt nur NULL. */
  function feld(
    name: keyof Hausdaten,
    beschriftung: string,
    hilfe: string,
    zusatz: { readonly platzhalter?: string; readonly gross?: boolean } = {},
  ): JSX.Element {
    return (
      <div className="feld" key={name}>
        <label className="feld__beschriftung" htmlFor={`gd-${name}`}>
          {beschriftung}
        </label>
        <input
          id={`gd-${name}`}
          type="text"
          value={daten[name] ?? ''}
          placeholder={zusatz.platzhalter}
          disabled={!darfPflegen}
          onChange={(e) => {
            const wert = zusatz.gross === true ? e.target.value.toUpperCase() : e.target.value;
            setzeDaten({ ...daten, [name]: wert === '' ? null : wert });
          }}
        />
        <p className="feld__hilfe">{hilfe}</p>
      </div>
    );
  }

  const steuerlichVollstaendig =
    (daten.steuernummer ?? '').trim() !== '' || (daten.ustidnr ?? '').trim() !== '';

  return (
    <div className="stapel">
      {fehler !== undefined && <Meldung fehler={fehler} />}
      {rueckmeldung !== '' && (
        <p className="hinweiszeile" role="status">
          {rueckmeldung}
        </p>
      )}

      {!bestand && (
        <div className="hinweiskarte hinweiskarte--warnung">
          <p>
            <strong>Es sind noch keine Geschäftsdaten hinterlegt.</strong> Ohne sie lässt sich
            keine Rechnung festschreiben — § 14 Abs. 4 UStG verlangt Name, Anschrift und eine
            Steuerkennung des Leistenden auf jedem Beleg.
          </p>
        </div>
      )}

      <Einstellungsseite>
        {/* ── Firma und Anschrift ───────────────────────────────────────── */}
        <Thema titel="Firma und Anschrift" zusatz="§ 14 Abs. 4 Nr. 1 UStG">
          <Feldraster>
            {feld(
              'firmierung',
              'Firmierung',
              'Der vollständige Name einschließlich der Rechtsform. Steht auf jedem Beleg '
              + '(Platzhalter haus.firmierung) und in der E-Rechnung als BT-27.',
            )}
            {feld(
              'strasse',
              'Straße und Hausnummer',
              'Teil der Anschrift auf dem Beleg (haus.anschrift) und BT-35 der E-Rechnung.',
            )}
            {feld('plz', 'Postleitzahl', 'BT-38. Die XRechnung verlangt sie (BR-DE-4).')}
            {feld('ort', 'Ort', 'BT-37.')}
            {feld(
              'land',
              'Land',
              'Zweistellig nach ISO 3166-1 alpha-2 — „DE", nicht „Deutschland". BT-40.',
              { platzhalter: 'DE', gross: true },
            )}
          </Feldraster>
        </Thema>

        {/* ── Steuerliche Angaben ───────────────────────────────────────── */}
        <Thema titel="Steuerliche Angaben" zusatz="§ 14 Abs. 4 Nr. 2 UStG">
          {!steuerlichVollstaendig && (
            <div className="hinweiskarte hinweiskarte--warnung">
              <p>
                <strong>Eine der beiden Angaben ist Pflicht.</strong> § 14 Abs. 4 Nr. 2 verlangt
                Steuernummer <em>oder</em> USt-IdNr.; ohne eine davon berechtigt die Rechnung den
                Empfänger nicht zum Vorsteuerabzug. Die Datenbank lässt den Satz auch nicht
                speichern.
              </p>
            </div>
          )}

          <Feldraster>
            {feld(
              'steuernummer',
              'Steuernummer',
              'Die vom Finanzamt vergebene Nummer. Steht auf dem Beleg (haus.steuernummer) und '
              + 'geht als BT-32 in die E-Rechnung.',
              { platzhalter: '123/456/78901' },
            )}
            {feld(
              'ustidnr',
              'Umsatzsteuer-Identifikationsnummer',
              'Für Geschäfte im EU-Ausland erforderlich. Beleg: haus.ustidnr, E-Rechnung: BT-31.',
              { platzhalter: 'DE123456789', gross: true },
            )}
          </Feldraster>
        </Thema>

        {/* ── Handelsregister ───────────────────────────────────────────── */}
        <Thema titel="Handelsregister und Vertretung" zusatz="§ 35a GmbHG, § 5 TMG">
          <Feldraster>
            {feld(
              'registergericht',
              'Registergericht',
              'Das Amtsgericht, das das Register führt. Auf Geschäftsbriefen einer GmbH '
              + 'vorgeschrieben (haus.registergericht).',
              { platzhalter: 'Amtsgericht Hannover' },
            )}
            {feld(
              'registernummer',
              'Registernummer (HRB)',
              'Mit dem Registerzeichen, also „HRB 12345" und nicht nur die Ziffern '
              + '(haus.registernummer).',
              { platzhalter: 'HRB 12345' },
            )}
            {feld(
              'geschaeftsfuehrung',
              'Geschäftsführung',
              'Die vertretungsberechtigten Personen. Erscheint im Belegfuß '
              + '(haus.geschaeftsfuehrung) und dient in der E-Rechnung als Ansprechpartner '
              + '(BT-41), den die XRechnung verlangt.',
            )}
          </Feldraster>
        </Thema>

        {/* ── Kontakt ───────────────────────────────────────────────────── */}
        <Thema titel="Kontakt" zusatz="BT-42, BT-43">
          <Feldraster>
            {feld(
              'telefon',
              'Telefon',
              'Die XRechnung verlangt eine Telefonnummer des Verkäufers (BT-42, BR-DE-5).',
              { platzhalter: '+49 511 1234567' },
            )}
            {feld(
              'email',
              'E-Mail',
              'Die elektronische Adresse des Hauses (BT-34, BT-43). In der XRechnung Pflicht. '
              + 'Nicht zu verwechseln mit den Absenderadressen der Postfächer — die stehen unter '
              + '„Postfächer".',
            )}
            {feld('webseite', 'Webseite', 'Erscheint auf dem Briefbogen, wenn die Vorlage sie führt.')}
          </Feldraster>
        </Thema>

        {/* ── Bankverbindung ────────────────────────────────────────────── */}
        <Thema titel="Bankverbindung" zusatz="BT-84 bis BT-86">

          <p className="feld__hilfe">
            Sie steht zweimal da, und das ist Absicht: Der Text erscheint auf dem Beleg
            (haus.bankverbindung), die Felder darunter liest der Empfänger einer E-Rechnung
            maschinell und zahlt danach.
          </p>

          <Feldraster>
            {feld(
              'bankverbindung',
              'Bankverbindung im Klartext',
              'Wie sie auf dem Beleg stehen soll — mit Bankname und lesbar gruppierter IBAN.',
              { platzhalter: 'Musterbank · IBAN DE02 1203 0000 0000 2020 51' },
            )}

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="gd-iban">
                IBAN
              </label>
              <input
                id="gd-iban"
                type="text"
                value={daten.iban ?? ''}
                placeholder="DE02120300000000202051"
                disabled={!darfPflegen}
                onChange={(e) =>
                  setzeDaten({
                    ...daten,
                    // Ohne Leerzeichen: Geschrieben wird sie mit Gruppen,
                    // gespeichert zusammenhängend — darauf soll niemand achten
                    // müssen.
                    iban: e.target.value.replaceAll(' ', '').toUpperCase() || null,
                  })
                }
              />
              <p className="feld__hilfe">
                BT-84. Ohne sie weist die XRechnung jede Überweisungsrechnung ab (BR-DE-19).
              </p>
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="gd-bic">
                BIC
              </label>
              <input
                id="gd-bic"
                type="text"
                value={daten.bic ?? ''}
                placeholder="BYLADEM1001"
                disabled={!darfPflegen}
                onChange={(e) =>
                  setzeDaten({
                    ...daten,
                    bic: e.target.value.replaceAll(' ', '').toUpperCase() || null,
                  })
                }
              />
              <p className="feld__hilfe">
                BT-86. Innerhalb der EU nicht erforderlich, außerhalb schon.
              </p>
            </div>

            {feld(
              'kontoinhaber',
              'Kontoinhaber',
              'BT-85. Bleibt das Feld leer, gilt die Firmierung.',
              { platzhalter: daten.firmierung },
            )}
          </Feldraster>
        </Thema>
      </Einstellungsseite>

      {darfPflegen && (
        <div className="knopfreihe">
          <button
            type="button"
            className="knopf knopf--haupt"
            disabled={laeuft}
            onClick={() =>
              void (async () => {
                setzeLaeuft(true);
                try {
                  await hausdatenSetzen(daten);
                  setzeFehler(undefined);
                  setzeRueckmeldung('Die Geschäftsdaten wurden gespeichert.');
                  await laden();
                } catch (ursache) {
                  setzeFehler(ursache as ApiFehler);
                } finally {
                  setzeLaeuft(false);
                }
              })()
            }
          >
            Geschäftsdaten speichern
          </button>
        </div>
      )}

      {/* BP-05: die eingefrorenen Angaben des Leistenden. */}
      <p className="feld__hilfe">
        Was hier steht, wird beim Festschreiben einer Rechnung <strong>eingefroren</strong>: Die
        Rechnung trägt die Angaben, die zu ihrem Zeitpunkt galten, und eine spätere Änderung wirkt
        nicht auf sie zurück.
      </p>
    </div>
  );
}
